// src/components/projects/overview/RecentTasks.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, CircularProgress, useTheme,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  TaskAlt as TaskIcon,
  ViewKanban as KanbanIcon
} from '@mui/icons-material';

import { EntityStatusBadge } from '../../core';
import SecureAvatar from '../../ui/SecureAvatar';
import { formatDate, isTaskOverdue } from '../../../utils/dateUtils';
import useTaskStore from '../../../stores/taskStore';
import { PLATFORM_OPTIONS } from '../../features/content/form-utils/contentFormData';
import Chip from '@mui/material/Chip';

const RecentTasks = ({ project, recentTasks, isLoading, canEdit }) => {
  const theme = useTheme();
  
  // Early return if no project
  if (!project) return null;
  
  return (
    <Paper sx={{ mb: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="h6">
          Recent Tasks
        </Typography>
        
        <Box>
          <Button
            variant="text"
            size="small"
            component={Link}
            to={`/projects/${project._id}/kanban`}
            startIcon={<KanbanIcon />}
            sx={{ mr: 1 }}
          >
            Board
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={`/projects/${project._id}/tasks`}
          >
            View All
          </Button>
        </Box>
      </Box>
      
      {isLoading && recentTasks.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : recentTasks.length > 0 ? (
        <Box>
          {recentTasks.map((task, index) => (
            <TaskItem 
              key={task._id} 
              task={task} 
              index={index} 
              totalItems={recentTasks.length} 
            />
          ))}
        </Box>
      ) : (
        <EmptyTasksState project={project} canEdit={canEdit} />
      )}
    </Paper>
  );
};

// Extracted Task Item component
const TaskItem = ({ task, index, totalItems }) => {
  const theme = useTheme();
  const isOverdue = isTaskOverdue(task);
  const { openTaskDrawer } = useTaskStore();
  
  // Open task drawer instead of navigating
  const handleTaskClick = (e) => {
    e.preventDefault();
    openTaskDrawer(task);
  };
  
  return (
    <Box 
      key={task._id}
      onClick={handleTaskClick}
      sx={{ 
        p: 2, 
        borderBottom: index < totalItems - 1 
          ? `1px solid ${theme.palette.divider}` 
          : 'none',
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.action.hover
        }
      }}
    >
      <TaskIcon 
        sx={{ 
          mr: 2,
          color: theme.palette.text.secondary
        }} 
      />
      
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle1">
          {task.title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
          <EntityStatusBadge status={task.status} entityType="task" size="small" />
          
          {task.dueDate && (
            <Typography 
              variant="body2" 
              color={isOverdue ? 'error' : 'text.secondary'}
            >
              Due: {formatDate(task.dueDate)}
            </Typography>
          )}
          
          {/* Platform chips */}
          {task.content && (task.content.platforms || task.content.distributions) && (
            <>
              {(() => {
                const platforms = task.content.platforms || (task.content.distributions ? task.content.distributions.map(d => d.platform) : []);
                return (
                  <>
                    {platforms.slice(0, 2).map((platformValue) => {
                      const platform = PLATFORM_OPTIONS.find(p => p.value === platformValue);
                      if (!platform) return null;
                      
                      return (
                        <Chip
                          key={platformValue}
                          label={platform.label}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            backgroundColor: platform.color,
                            color: 'white',
                            '& .MuiChip-label': {
                              px: 0.8
                            }
                          }}
                        />
                      );
                    })}
                    {platforms.length > 2 && (
                      <Typography variant="caption" color="text.secondary">
                        +{platforms.length - 2}
                      </Typography>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </Box>
      </Box>
      
      {task.assignedTo && (
        <Tooltip title={`Assigned to ${task.assignedTo.firstName} ${task.assignedTo.lastName}`}>
          <SecureAvatar
            src={task.assignedTo.photo}
            alt={`${task.assignedTo.firstName} ${task.assignedTo.lastName}`}
            sx={{ width: 32, height: 32 }}
          >
            {task?.assignedTo?.firstName?.[0]}
          </SecureAvatar>
        </Tooltip>
      )}
    </Box>
  );
};

// Updated empty state component to use task drawer instead of navigation
const EmptyTasksState = ({ project, canEdit }) => {
  const { openTaskDrawer } = useTaskStore();
  
  // Handler to open task creation drawer with project context
  const handleCreateTask = () => {
    openTaskDrawer({
      projectId: project._id,
      // You can add additional default values here if needed
      status: 'to_do',
      priority: 'medium'
    });
  };
  
  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="body1" color="text.secondary">
        No tasks found
      </Typography>
      
      {canEdit && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ mt: 1 }}
          onClick={handleCreateTask}
        >
          Add Task
        </Button>
      )}
    </Box>
  );
};

export default RecentTasks;